import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { DemoRequestErrors, DemoRequestField, DemoRequestInput } from '@/lib/demo-request'

import { useEffect, useRef, useState } from 'react'
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

import {
    DEMO_REQUEST_FIELDS,
    DEMO_REQUEST_LIMITS,
    TEAM_SIZES,
    TEAM_SIZE_LABELS,
    normalizeDemoRequest,
    validateDemoRequest
} from '@/lib/demo-request'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const EMPTY_FORM: DemoRequestInput = {
    name: '',
    business: '',
    phone: '',
    email: '',
    teamSize: '',
    message: ''
}

const fieldClassName = 'h-12 rounded-xl bg-background px-4 text-base md:text-[0.95rem]'

interface FieldProps {
    id: string
    label: string
    optional?: boolean
    error?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({ id, label, optional, error, children }): ReactNode => {
    return (
        <div className='space-y-2'>
            <Label htmlFor={id} className='gap-1.5 text-sm font-semibold text-foreground'>
                {label}

                {optional && (
                    <span className='font-normal text-muted-foreground'>
                        (optional)
                    </span>
                )}
            </Label>

            {children}

            {error && (
                <p id={`${id}-error`} className='flex items-center gap-1.5 text-sm font-medium text-destructive'>
                    <AlertCircle className='size-4 shrink-0' />
                    {error}
                </p>
            )}
        </div>
    )
}

const DemoForm: FC = (): ReactNode => {
    const [values, setValues] = useState<DemoRequestInput>(EMPTY_FORM)
    const [errors, setErrors] = useState<DemoRequestErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [formError, setFormError] = useState('')
    const [submittedName, setSubmittedName] = useState('')
    const [website, setWebsite] = useState('')
    const successRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (field: DemoRequestField) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) {
            setErrors(current => {
                const next = { ...current }

                delete next[field]

                return next
            })
        }
    }

    const describedBy = (field: DemoRequestField): string | undefined => errors[field] ? `demo-${field}-error` : undefined

    const focusFirstError = (nextErrors: DemoRequestErrors): void => {
        const first = DEMO_REQUEST_FIELDS.find(field => nextErrors[field])

        if (!first) return

        const target = first === 'teamSize'
            ? document.querySelector<HTMLInputElement>('input[name="teamSize"]')
            : document.getElementById(`demo-${first}`)

        target?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const input = normalizeDemoRequest(values)
        const nextErrors = validateDemoRequest(input)

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors)
            setFormError('')
            focusFirstError(nextErrors)

            return
        }

        setStatus('submitting')
        setFormError('')

        try {
            const response = await fetch('/api/demo-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...input, website })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean, message?: string, errors?: DemoRequestErrors } | null

            if (response.ok && data?.ok) {
                setSubmittedName(input.name.split(' ')[0] ?? '')
                setValues(EMPTY_FORM)
                setErrors({})
                setStatus('success')

                return
            }

            if (data?.errors) {
                setErrors(data.errors)
                focusFirstError(data.errors)
            }

            setFormError(data?.message || "Something went wrong on our side. Please try again.")
            setStatus('error')
        }

        catch {
            setFormError("We couldn't reach the server. Check your connection and try again.")
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='rounded-3xl bg-white p-8 text-center text-foreground shadow-[0_40px_80px_-40px_rgb(0_0_0/0.6)] outline-none sm:p-10'
            >
                <span className='mx-auto grid size-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-600'>
                    <CheckCircle2 className='size-7' />
                </span>

                <h3 className='mt-6 font-heading text-2xl font-bold tracking-tight'>
                    Request received
                </h3>

                <p className='mx-auto mt-3 max-w-sm text-base leading-relaxed text-muted-foreground'>
                    {submittedName ? `Thanks, ${submittedName}. ` : "Thanks. "}
                    We&apos;ll be in touch soon to set up your Elenix demo.
                </p>

                <Button
                    type='button'
                    variant='outline'
                    size='pill'
                    className='mt-8'
                    onClick={() => setStatus('idle')}
                >
                    Send another request
                </Button>
            </div>
        )
    }

    const submitting = status === 'submitting'

    return (
        <form
            noValidate
            onSubmit={handleSubmit}
            aria-labelledby='demo-form-title'
            className='rounded-3xl bg-white p-6 text-foreground shadow-[0_40px_80px_-40px_rgb(0_0_0/0.6)] sm:p-8'
        >
            <h3 id='demo-form-title' className='font-heading text-xl font-bold tracking-tight'>
                Request your demo
            </h3>

            <p className='mt-1 text-sm text-muted-foreground'>
                Takes less than a minute.
            </p>

            <div className='mt-6 grid gap-5 sm:grid-cols-2'>
                <Field id='demo-name' label="Your name" error={errors.name}>
                    <Input
                        id='demo-name'
                        name='name'
                        autoComplete='name'
                        placeholder="Full name"
                        required
                        maxLength={DEMO_REQUEST_LIMITS.name}
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={describedBy('name')}
                        className={fieldClassName}
                    />
                </Field>

                <Field id='demo-business' label="Business name" error={errors.business}>
                    <Input
                        id='demo-business'
                        name='business'
                        autoComplete='organization'
                        placeholder="e.g. Sharma Traders"
                        required
                        maxLength={DEMO_REQUEST_LIMITS.business}
                        value={values.business}
                        onChange={update('business')}
                        aria-invalid={Boolean(errors.business)}
                        aria-describedby={describedBy('business')}
                        className={fieldClassName}
                    />
                </Field>

                <Field id='demo-phone' label="Mobile number" error={errors.phone}>
                    <Input
                        id='demo-phone'
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        placeholder="98765 43210"
                        required
                        maxLength={DEMO_REQUEST_LIMITS.phone}
                        value={values.phone}
                        onChange={update('phone')}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={describedBy('phone')}
                        className={cn(fieldClassName, 'tabular-nums')}
                    />
                </Field>

                <Field id='demo-email' label="Email" optional error={errors.email}>
                    <Input
                        id='demo-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        placeholder="you@business.com"
                        maxLength={DEMO_REQUEST_LIMITS.email}
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('email')}
                        className={fieldClassName}
                    />
                </Field>
            </div>

            <fieldset className='mt-5' aria-describedby={describedBy('teamSize')}>
                <legend className='flex items-center gap-1.5 text-sm font-semibold text-foreground'>
                    Team size

                    <span className='font-normal text-muted-foreground'>
                        (optional)
                    </span>
                </legend>

                <div className='mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4'>
                    {TEAM_SIZES.map(size => (
                        <label key={size} className='relative cursor-pointer'>
                            <input
                                type='radio'
                                name='teamSize'
                                value={size}
                                checked={values.teamSize === size}
                                onChange={update('teamSize')}
                                className='peer sr-only'
                            />

                            <span className='flex h-11 items-center justify-center rounded-xl border border-input bg-background text-sm font-semibold text-muted-foreground transition-colors hover:border-brand-300 peer-checked:border-primary peer-checked:bg-brand-50 peer-checked:text-brand-700 peer-focus-visible:ring-3 peer-focus-visible:ring-ring/50'>
                                {TEAM_SIZE_LABELS[size]}
                            </span>
                        </label>
                    ))}
                </div>

                {errors.teamSize && (
                    <p id='demo-teamSize-error' className='mt-2 flex items-center gap-1.5 text-sm font-medium text-destructive'>
                        <AlertCircle className='size-4 shrink-0' />
                        {errors.teamSize}
                    </p>
                )}
            </fieldset>

            <div className='mt-5'>
                <Field id='demo-message' label="Anything we should know?" optional error={errors.message}>
                    <Textarea
                        id='demo-message'
                        name='message'
                        rows={3}
                        placeholder="Tell us about your business and what you'd like to see."
                        maxLength={DEMO_REQUEST_LIMITS.message}
                        value={values.message}
                        onChange={update('message')}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={describedBy('message')}
                        className='min-h-24 rounded-xl bg-background px-4 py-3 text-base md:text-[0.95rem]'
                    />
                </Field>
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] size-px overflow-hidden'>
                <label htmlFor='demo-website'>
                    Leave this field empty
                </label>

                <input
                    id='demo-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={website}
                    onChange={event => setWebsite(event.target.value)}
                />
            </div>

            {formError && (
                <p role='alert' className='mt-5 flex items-start gap-2 rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive'>
                    <AlertCircle className='mt-0.5 size-4 shrink-0' />
                    {formError}
                </p>
            )}

            <Button
                type='submit'
                size='pill-lg'
                disabled={submitting}
                className='mt-6 w-full shadow-[0_12px_28px_-12px_rgb(53_102_219/0.9)]'
            >
                {submitting ? (
                    <>
                        <Loader2 className='size-4 animate-spin' />
                        Sending your request
                    </>
                ) : (
                    <>
                        Book my demo
                        <ArrowRight className='size-4' />
                    </>
                )}
            </Button>

            <p className='mt-4 text-center text-xs leading-relaxed text-muted-foreground'>
                We&apos;ll only use these details to arrange your demo.
            </p>
        </form>
    )
}

export default DemoForm
